import { useState, useEffect } from 'react';
import api from '../../API.js';
import styles from '../../styles/agents.module.css';
import PubMedToolConfig from './tools/PubMedToolConfig';
import WebsiteSearcherToolConfig from './tools/WebsiteSearcherToolConfig';

function ToolSelector({ selectedTools = [], onChange, disabled = false }) {
    const [availableToolTypes, setAvailableToolTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAddTool, setShowAddTool] = useState(false);
    const [newToolType, setNewToolType] = useState('');

    useEffect(() => {
        fetchToolTypes();
    }, []);

    const fetchToolTypes = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await api.get('agent/tools/types');
            
            if (response.status === 200 && response.data.success) {
                setAvailableToolTypes(response.data.toolTypes || []);
            } else {
                throw new Error(`Failed to fetch tool types: ${response.status}`);
            }
        } catch (err) {
            console.error('Error fetching tool types:', err);
            setError('Failed to load tool types: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAddTool = (toolType) => {
        if (disabled) return;
        
        const toolTypeDef = availableToolTypes.find(t => t.type === toolType);
        if (!toolTypeDef) return;

        // Create default config based on tool type
        let defaultConfig = {};
        if (toolType === 'rag') {
            defaultConfig = { collections: [] };
        } else if (toolType === 'pubmed') {
            defaultConfig = {
                maxResults: 10,
                sort: 'relevance'
            };
        } else if (toolType === 'website_searcher') {
            defaultConfig = {
                searchEngine: 'duckduckgo',
                maxResults: 10
            };
        }

        const newTool = {
            type: toolType,
            config: defaultConfig,
            toolId: `tool_${Date.now()}`
        };

        onChange([...selectedTools, newTool]);
        setShowAddTool(false);
        setNewToolType('');
    };

    const handleRemoveTool = (toolId) => {
        if (disabled) return;
        onChange(selectedTools.filter(t => t.toolId !== toolId));
    };

    const handleUpdateTool = (toolId, updatedTool) => {
        if (disabled) return;
        onChange(selectedTools.map(t => t.toolId === toolId ? updatedTool : t));
    };

    const renderToolConfig = (tool) => {
        if (tool.type === 'rag') {
            return (
                <div style={{ padding: '1em', background: '#f6f8fa', borderRadius: '6px' }}>
                    <div style={{ fontWeight: 500, marginBottom: '0.5em' }}>
                        Knowledge Base (RAG)
                    </div>
                    <div style={{ fontSize: '13px', color: '#586069' }}>
                        Collections: {tool.config?.collections?.length || 0}
                        {tool.config?.collections?.length > 0 && (
                            <span> ({tool.config.collections.join(', ')})</span>
                        )}
                    </div>
                </div>
            );
        } else if (tool.type === 'pubmed') {
            return (
                <PubMedToolConfig
                    config={tool.config || {}}
                    onChange={(config) => handleUpdateTool(tool.toolId, { ...tool, config })}
                    disabled={disabled}
                />
            );
        } else if (tool.type === 'website_searcher') {
            return (
                <WebsiteSearcherToolConfig
                    config={tool.config || {}}
                    onChange={(config) => handleUpdateTool(tool.toolId, { ...tool, config })}
                    disabled={disabled}
                />
            );
        }
        return null;
    };

    if (loading) {
        return <div className={styles.loading}>Loading tool types...</div>;
    }

    return (
        <div className={styles.formGroup}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75em' }}>
                <label className={styles.formLabel}>
                    Tools
                </label>
                {!disabled && (
                    <button
                        type="button"
                        onClick={() => setShowAddTool(!showAddTool)}
                        className={styles.button}
                        style={{ padding: '0.5em 1em', fontSize: '13px' }}
                    >
                        {showAddTool ? 'Cancel' : '+ Add Tool'}
                    </button>
                )}
            </div>

            {showAddTool && (
                <div style={{
                    marginBottom: '1em',
                    padding: '1em',
                    background: '#f6f8fa',
                    borderRadius: '6px',
                    border: '1px solid #e1e4e8'
                }}>
                    <label className={styles.formLabel} style={{ marginBottom: '0.5em' }}>
                        Select Tool Type
                    </label>
                    <select
                        value={newToolType}
                        onChange={(e) => setNewToolType(e.target.value)}
                        className={styles.formInput}
                        style={{ marginBottom: '0.75em' }}
                    >
                        <option value="">Choose a tool...</option>
                        {availableToolTypes.map(toolType => (
                            <option key={toolType.type} value={toolType.type}>
                                {toolType.name} - {toolType.description}
                            </option>
                        ))}
                    </select>
                    {newToolType && (
                        <button
                            type="button"
                            onClick={() => handleAddTool(newToolType)}
                            className={`${styles.button} ${styles.buttonPrimary}`}
                            style={{ padding: '0.5em 1em', fontSize: '13px' }}
                        >
                            Add {availableToolTypes.find(t => t.type === newToolType)?.name}
                        </button>
                    )}
                </div>
            )}

            {selectedTools.length === 0 ? (
                <div style={{
                    padding: '2em',
                    textAlign: 'center',
                    color: '#586069',
                    background: '#f6f8fa',
                    borderRadius: '6px',
                    border: '1px dashed #d1d5db'
                }}>
                    No tools configured. Add a tool to enable additional capabilities.
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
                    {selectedTools.map(tool => {
                        const toolTypeDef = availableToolTypes.find(t => t.type === tool.type);
                        return (
                            <div
                                key={tool.toolId}
                                style={{
                                    padding: '1em',
                                    border: '1px solid #e1e4e8',
                                    borderRadius: '6px',
                                    background: 'white'
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75em' }}>
                                    <div>
                                        <div style={{ fontWeight: 600, color: '#24292e' }}>
                                            {toolTypeDef?.name || tool.type}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#586069', marginTop: '0.25em' }}>
                                            {toolTypeDef?.description}
                                        </div>
                                    </div>
                                    {!disabled && (
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveTool(tool.toolId)}
                                            style={{
                                                padding: '0.25em 0.75em',
                                                border: '1px solid #dc3545',
                                                borderRadius: '4px',
                                                background: 'white',
                                                color: '#dc3545',
                                                fontSize: '12px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                                {renderToolConfig(tool)}
                            </div>
                        );
                    })}
                </div>
            )}

            {error && (
                <div className={styles.formHelp} style={{ color: '#dc3545' }}>
                    {error}
                </div>
            )}
        </div>
    );
}

export default ToolSelector;
